<template>
  <form class="onb__form" @submit.prevent="submit">
    <p v-if="error" class="sh-card__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ error }}</span>
    </p>

    <v-text-field
      v-model="legalName"
      :label="t('onboarding.company.legalName')"
      :hint="t('onboarding.company.legalNameHint')"
      persistent-hint
      variant="outlined"
      density="comfortable"
      autocomplete="organization"
      autofocus
      :disabled="busy"
    />

    <v-text-field
      v-model="registrationNumber"
      :label="t('onboarding.company.registrationNumber')"
      :hint="t('onboarding.company.registrationNumberHint')"
      persistent-hint
      variant="outlined"
      density="comfortable"
      autocomplete="off"
      :disabled="busy"
    />

    <v-text-field
      v-model="vatNumber"
      :label="t('onboarding.company.vatNumber')"
      variant="outlined"
      density="comfortable"
      autocomplete="off"
      :disabled="busy"
      hide-details
    />

    <v-autocomplete
      v-model="countryCode"
      :items="countries"
      item-title="title"
      item-value="value"
      :label="t('onboarding.company.country')"
      :hint="t('onboarding.company.countryHint')"
      persistent-hint
      variant="outlined"
      density="comfortable"
      autocomplete="off"
      :disabled="busy"
    />

    <v-autocomplete
      v-model="baseCurrency"
      :items="currencies"
      item-title="title"
      item-value="value"
      :label="t('onboarding.company.baseCurrency')"
      variant="outlined"
      density="comfortable"
      autocomplete="off"
      :disabled="busy"
      hide-details
    />

    <v-text-field
      v-model="contactName"
      :label="t('onboarding.contactName')"
      variant="outlined"
      density="comfortable"
      autocomplete="name"
      :disabled="busy"
      hide-details
    />

    <v-text-field
      v-model="contactPhone"
      :label="t('onboarding.company.contactPhone')"
      type="tel"
      variant="outlined"
      density="comfortable"
      autocomplete="tel"
      inputmode="tel"
      :disabled="busy"
      hide-details
    />

    <v-btn
      type="submit"
      color="primary"
      size="large"
      block
      class="onb__submit"
      :loading="busy"
      :disabled="!canSubmit"
      >{{ t("onboarding.company.submit") }}</v-btn
    >
  </form>
</template>

<script lang="ts">
/**
 * PAC-1374 — the company half of the sign-up screen, rendered by
 * Onboarding.vue when the tenant app's `signupForm` is "COMPANY" (a WMS front
 * door such as packhouse.metaship.ai). Onboarding.vue keeps the shared frame —
 * header, the NEEDS_REVIEW reference panel, the sign-out escape — so the Boer
 * farm flow is untouched.
 *
 * Required: legal name, the company's (legal-home) country and its base
 * currency. Registration and VAT numbers are optional search keys: the backend
 * uses them to find an existing organisation, never as proof of owning it.
 * Each SITE's country comes from its POI, which is the next screen.
 */
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { countryOptions, currencyOptions } from "@/shell/signupOptions";

const MIN_LEGAL_NAME_LENGTH = 2;

export default defineComponent({
  name: "CompanySignupForm",
  emits: ["review"],
  setup(_props, { emit }) {
    const { t, locale } = useI18n();
    const session = useSessionStore();

    const legalName = ref("");
    const registrationNumber = ref("");
    const vatNumber = ref("");
    const countryCode = ref<string | null>(null);
    const baseCurrency = ref<string | null>(null);
    const contactName = ref("");
    const contactPhone = ref("");
    const busy = ref(false);
    const error = ref<string | null>(null);

    const countries = computed(() => countryOptions(locale.value));
    const currencies = computed(() => currencyOptions(locale.value));

    const canSubmit = computed(
      () =>
        legalName.value.trim().length >= MIN_LEGAL_NAME_LENGTH &&
        !!countryCode.value &&
        !!baseCurrency.value &&
        !busy.value,
    );

    async function submit(): Promise<void> {
      if (!canSubmit.value) return;
      busy.value = true;
      error.value = null;
      try {
        const result = await session.completeCompanySignup({
          legalName: legalName.value.trim(),
          registrationNumber: registrationNumber.value.trim() || undefined,
          vatNumber: vatNumber.value.trim() || undefined,
          countryCode: countryCode.value as string,
          baseCurrency: baseCurrency.value as string,
          contactName: contactName.value.trim() || undefined,
          contactPhone: contactPhone.value.trim() || undefined,
        });
        // CREATED/CLAIMED: resolveOrgAccess moved the session on and App.vue
        // swaps the screen. Only a review has anything left to say.
        if (result.outcome === "NEEDS_REVIEW") emit("review", result.reference ?? "");
      } catch (e) {
        error.value = (e as Error)?.message?.replace(/^POST \S+ failed: \d+\s*/, "") || t("onboarding.failed");
      } finally {
        busy.value = false;
      }
    }

    return {
      t,
      legalName,
      registrationNumber,
      vatNumber,
      countryCode,
      baseCurrency,
      contactName,
      contactPhone,
      countries,
      currencies,
      busy,
      error,
      canSubmit,
      submit,
    };
  },
});
</script>
